import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/useAuth";
import { apiGet, getApiErrorCode, getApiErrorMessage, type ApiResult } from "@/api";
import { tokenQuery } from "./query-options";

export interface LeadSignalItem {
  id: string;
  captureId: string;
  signalType: string;
  payload?: Record<string, unknown>;
  createdAt: string;
}

export interface CompanyGraphItem {
  domain: string;
  name: string;
  captureCount: number;
  lastActivityAt: string | null;
}

export interface LeadSignalsQueryData {
  signals: LeadSignalItem[];
  premiumRequired: boolean;
}

export interface CompanyGraphQueryData {
  companies: CompanyGraphItem[];
  premiumRequired: boolean;
}

/**
 * Turns a `/lead-signals` response into either the signals or the upgrade offer, or throws.
 *
 * Separated from the query to be testable; it knows nothing about React or the query cache.
 *
 * `403 premium_required` is the one failure that is NOT an error: it is the product telling a
 * Free user what Pro would give them. Reported as `premiumRequired: false`, the page falls back
 * to an ordinary empty state — "no signals yet" — which is indistinguishable from a Pro user who
 * genuinely has none. Two things then disappear at once: the upgrade offer, and the
 * `paywall_shown` event both routes fire off this same flag. The conversion prompt and the
 * metric that would reveal its absence go together, which is what makes this one quiet.
 */
export function selectLeadSignals(res: ApiResult<{ signals?: LeadSignalItem[] }>): LeadSignalsQueryData {
  if (!res.ok) {
    if (res.status === 403 && getApiErrorCode(res) === "premium_required") {
      return { signals: [], premiumRequired: true };
    }
    throw new Error(getApiErrorMessage(res, "Lead Signals konnten nicht geladen werden"));
  }
  return { signals: res.data.signals ?? [], premiumRequired: false };
}

export function leadSignalsQueryOptions(token: string | null) {
  return tokenQuery(["lead-signals"], token, async (t) =>
    selectLeadSignals(await apiGet<{ signals?: LeadSignalItem[] }>("/lead-signals", t)),
  );
}

export function useLeadSignals() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(leadSignalsQueryOptions(token));
}

/** Same decision as selectLeadSignals, for the other Pro feature behind the same gate. */
export function selectCompanyGraph(res: ApiResult<{ companies?: CompanyGraphItem[] }>): CompanyGraphQueryData {
  if (!res.ok) {
    if (res.status === 403 && getApiErrorCode(res) === "premium_required") {
      return { companies: [], premiumRequired: true };
    }
    throw new Error(getApiErrorMessage(res, "Company Graph konnte nicht geladen werden"));
  }
  return { companies: res.data.companies ?? [], premiumRequired: false };
}

export function companyGraphQueryOptions(token: string | null) {
  return tokenQuery(["company-graph"], token, async (t) =>
    selectCompanyGraph(await apiGet<{ companies?: CompanyGraphItem[] }>("/company-graph", t)),
  );
}

export function useCompanyGraph() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(companyGraphQueryOptions(token));
}
